"use client";

import { ReactNode, useEffect } from "react";
import { toast } from "@/components/AppToaster";
import { readSession } from "@/lib/session";

const LOCAL_API = "http://localhost:5002";
const SESSION_REPLACED_HINT = "another device";
const INACTIVITY_MS = 10 * 60 * 1000;
let csrfTokenPromise: Promise<string> | null = null;
let fetchPatched = false;
let handlingSessionExpiry = false;
let inactivityTimer: number | null = null;

function browserApiOrigin() {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return LOCAL_API;
  return window.location.origin;
}

function rewriteApiUrl(url: string) {
  if (!url.startsWith(LOCAL_API)) return url;
  const origin = browserApiOrigin();
  if (origin === LOCAL_API) return url;
  return `${origin}${url.slice(LOCAL_API.length)}`;
}

function loadCsrfToken(original: typeof window.fetch) {
  if (!csrfTokenPromise) {
    csrfTokenPromise = original(`${browserApiOrigin()}/api/csrf-token`, { credentials: "include" })
      .then((response) => response.json())
      .then((data: { csrfToken?: string }) => {
        if (!data.csrfToken) throw new Error("Missing security token.");
        return data.csrfToken;
      })
      .catch((error) => {
        csrfTokenPromise = null;
        throw error;
      });
  }
  return csrfTokenPromise;
}

function requestUrl(input: RequestInfo | URL) {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function clearClientSessionCookie() {
  document.cookie = "avt_session=; Path=/; Max-Age=0; SameSite=Strict";
}

function logoutBeaconUrl() {
  return `${browserApiOrigin()}/api/auth/logout?beacon=1`;
}

/** Kill server session when the tab/browser is actually leaving (not Next.js Link navigations). */
function beaconLogoutOnExit() {
  if (!readSession()) return;
  clearClientSessionCookie();
  try {
    // Empty body keeps this a "simple" request so sendBeacon works cross-origin without preflight.
    navigator.sendBeacon(logoutBeaconUrl());
  } catch {
    // Best-effort; session cookie without maxAge still drops when the browser closes.
  }
}

async function logoutForInactivity() {
  if (!readSession()) return;
  clearClientSessionCookie();
  try {
    await fetch(`${browserApiOrigin()}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Continue redirect even if network fails.
  }
  if (window.location.pathname !== "/login") {
    toast.error("Logged out due to inactivity");
    window.location.replace("/login");
  }
}

function clearInactivityTimer() {
  if (inactivityTimer != null) {
    window.clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
}

function armInactivityTimer() {
  clearInactivityTimer();
  if (!readSession()) return;
  inactivityTimer = window.setTimeout(() => {
    void logoutForInactivity();
  }, INACTIVITY_MS);
}

function handleSessionReplaced() {
  if (handlingSessionExpiry) return;
  if (window.location.pathname === "/login") {
    clearClientSessionCookie();
    return;
  }
  handlingSessionExpiry = true;
  clearClientSessionCookie();
  toast.error("You have been logged out because you logged in from another device");
  window.location.replace("/login");
}

async function maybeHandleSessionExpiry(url: string, response: Response) {
  if (!url.startsWith(browserApiOrigin()) || response.status !== 401) return;
  if (url.includes("/api/auth/login") || url.includes("/api/auth/google")) return;
  const data = await response.clone().json().catch(() => null);
  const message = String(data?.message || "").toLowerCase();
  if (message.includes(SESSION_REPLACED_HINT)) {
    handleSessionReplaced();
  }
}

function isTypingField(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

function isBlockedShortcut(event: KeyboardEvent) {
  if (event.key === "F12" || event.code === "F12" || event.keyCode === 123) return true;
  const ctrl = event.ctrlKey || event.metaKey;
  if (!ctrl) return false;
  if (event.shiftKey && (event.code === "KeyI" || event.code === "KeyJ" || event.code === "KeyC")) return true;
  if (!event.shiftKey && (event.code === "KeyU" || event.code === "KeyS" || event.code === "KeyP")) return true;
  return false;
}

function installApiFetch() {
  if (fetchPatched || typeof window === "undefined") return;
  fetchPatched = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const rawUrl = requestUrl(input);
    const url = rewriteApiUrl(rawUrl);
    const method = String(init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase();
    const mutating = method === "POST" || method === "PATCH" || method === "PUT" || method === "DELETE";
    const send = (nextInit?: RequestInit) => {
      if (url === rawUrl) return original(input, nextInit);
      if (input instanceof Request) return original(new Request(url, input), nextInit);
      return original(url, nextInit);
    };

    let response: Response;
    if (!url.startsWith(browserApiOrigin()) || !mutating) {
      response = await send(init);
    } else {
      const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
      headers.set("x-csrf-token", await loadCsrfToken(original));
      response = await send({ ...init, headers, credentials: "include" });
      if (response.status === 403) {
        const data = await response.clone().json().catch(() => null);
        if (String(data?.message || "").toLowerCase().includes("security token")) {
          csrfTokenPromise = null;
          headers.set("x-csrf-token", await loadCsrfToken(original));
          response = await send({ ...init, headers, credentials: "include" });
        }
      }
    }

    await maybeHandleSessionExpiry(url, response);
    return response;
  };
}

installApiFetch();

export default function SecurityWrapper({ children }: { children: ReactNode }) {
  useEffect(() => {
    installApiFetch();

    function onContextMenu(event: MouseEvent) {
      if (isTypingField(event.target)) return;
      event.preventDefault();
    }

    function onCopy(event: ClipboardEvent) {
      if (isTypingField(event.target)) return;
      event.preventDefault();
    }

    function onDragStart(event: DragEvent) {
      if (isTypingField(event.target)) return;
      event.preventDefault();
    }

    function onKeyDownSecurity(event: KeyboardEvent) {
      if (!isBlockedShortcut(event)) return;
      event.preventDefault();
    }

    function onPageHide(event: PageTransitionEvent) {
      // bfcache freeze — page may come back; do not kill the session.
      if (event.persisted) return;
      // Do NOT logout on refresh or in-app navigation. Session ends via Logout button,
      // inactivity timeout, or when the browser drops session cookies on full close.
    }

    function onVisibilityChange() {
      // Do not logout on tab blur (user switching apps). Only arm/pause inactivity.
      if (document.visibilityState === "hidden") {
        // Keep inactivity timer running while hidden.
        return;
      }
      armInactivityTimer();
    }

    function onUserActivity() {
      armInactivityTimer();
    }

    armInactivityTimer();

    window.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("copy", onCopy);
    window.addEventListener("dragstart", onDragStart);
    window.addEventListener("keydown", onKeyDownSecurity);
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("mousemove", onUserActivity, { passive: true });
    window.addEventListener("mousedown", onUserActivity, { passive: true });
    window.addEventListener("keydown", onUserActivity);
    window.addEventListener("touchstart", onUserActivity, { passive: true });
    window.addEventListener("scroll", onUserActivity, { passive: true });
    window.addEventListener("click", onUserActivity, { passive: true });

    return () => {
      clearInactivityTimer();
      window.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("copy", onCopy);
      window.removeEventListener("dragstart", onDragStart);
      window.removeEventListener("keydown", onKeyDownSecurity);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("mousemove", onUserActivity);
      window.removeEventListener("mousedown", onUserActivity);
      window.removeEventListener("keydown", onUserActivity);
      window.removeEventListener("touchstart", onUserActivity);
      window.removeEventListener("scroll", onUserActivity);
      window.removeEventListener("click", onUserActivity);
    };
  }, []);

  return children;
}
